import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { GetStaticPathsResult, GetStaticProps } from 'next';

import { Season } from 'types/seasons';
import TwoColumnLayout from 'layouts/TwoColumn';
import Page from 'layouts/Page';

import { programApi } from 'lib/api/programApi';
import { organizationApi } from 'lib/api/organizationApi';
import { withStaticProps } from 'lib/withStaticProps';
import { i18n } from 'lib/i18n';
import { seasonApi } from 'lib/api/seasonApi';
// import Catalog from 'components/Catalog/Catalog';
import LongDescription from 'components/shared/longDescription/LongDescription';
import ContactDetails from 'components/shared/ContactDetails/ContactDetails';
import Hero from 'components/shared/Hero/Hero';
import SeasonPicker from 'components/shared/SeasonPicker/SeasonPicker';
import { getSportsIconsNames } from 'lib/sports';
import { contactDataInit } from 'lib/organization';
import { useBranding } from 'hooks/useBranding';
import { useDisclaimer } from 'hooks/useDisclaimer';
import { getTags } from 'lib/heroUtils';
import { css } from '@emotion/react';
import {
	activeSeason,
	futureSeason,
	isRegistrationWindowOpenToAny,
	pastSeason,
} from 'lib/seasonUtils';
import ProgramSeasons from 'components/shared/Season/ProgramSeasons';
import { getResourceSettingValueByKey } from '../../lib/resourceSettings';
import { ESessionResourceSettingKey } from '../../types/resource-settings';
import { isLater, TimeUnit } from '@bondsports/date-time';

const heroIconCss = css`
	div#heroIcons {
		display: flex;
	}
	div#heroTags {
		margin-top: 6px;
	}
`;

function Programs() {
	const router = useRouter();
	const { programId } = router.query;

	const [programMainMedia, setProgramMainMedia] = useState<string>('');
	const [heroTitle, setHeroTitle] = useState<string>('');
	const [heroDescription, setHeroDescription] = useState<string>('');
	const [contactData, setContactData] = useState({ ...contactDataInit });
	const [futureSeasons, setFutureSeasons] = useState<Season[]>([]);
	const [pastSeasons, setPastSeasons] = useState<Season[]>([]);
	const [activeSeasons, setActiveSeasons] = useState<Season[]>([]);

	const [activeRegitrationSeasons, setActiveRegitrationSeasons] = useState<Season[]>([]);
	const [programSport, setProgramSport] = useState<number | null>(null);
	// const [otherPrograms, setOtherPrograms] = useState<Program[]>([]);
	// const [programType, setProgramType] = useState<string>();
	const { setBranding } = useBranding();
	const { setDisclaimer } = useDisclaimer();

	const [isLoading, setLoading] = useState<boolean>(true);
	const [isRegistrationEnabled, setRegistrationEnabled] = useState<boolean>(true);
	const [programLongDescription, setProgramLongDescription] = useState<string>('');
	const [tags, setTags] = useState<string[]>([]);
	const [isHideEventTimes, setIsHideEventTimes] = useState<boolean>(true);

	/* function getProgramTypeString(programType: ProgramTypesEnum) {
    switch (programType) {
      case ProgramTypesEnum.CAMP:
        return i18n.programs.type.camps;
      case ProgramTypesEnum.CLASS:
        return i18n.programs.type.classes;
      case ProgramTypesEnum.CLINIC:
        return i18n.programs.type.clinics;
      case ProgramTypesEnum.LESSON:
        return i18n.programs.type.lessons;
      case ProgramTypesEnum.TOURNAMENT:
        return i18n.programs.type.tournaments;
      case ProgramTypesEnum.CLUB:
        return i18n.programs.type.clubs;
    }
  }*/

	useEffect(() => {
		async function fetchProgramData() {
			if (!programId || Array.isArray(programId)) {
				return;
			}

			const programData = await programApi.getProgramData(Number(programId));
			// setProgramType(getProgramTypeString(programData.type));
			setProgramLongDescription(programData.longDescription ?? '');
			setProgramMainMedia(programData.mainMedia?.url ?? '');
			setHeroTitle(programData.name);
			setHeroDescription(programData.description ?? '');
			setProgramSport(programData.sport);

			const seasonData: Season[] = await seasonApi.getProgramSeasons(Number(programId));

			setTags(getTags(programData) as string[]);

			const organization = await organizationApi.getOrganizationData(programData.organizationId);

			setBranding(organization.brandings);
			setDisclaimer(organization.disclaimer);

			if (seasonData) {
				setFutureSeasons(seasonData.filter(season => futureSeason(season)));
				setActiveSeasons(seasonData.filter(season => activeSeason(season)));
				setPastSeasons(seasonData.filter(season => pastSeason(season)));

				const activeRegitrationSeasons = seasonData.filter(
					season => season.registrationEndDate && !isLater(new Date(), season.registrationEndDate, TimeUnit.DAY)
				);
				setActiveRegitrationSeasons(activeRegitrationSeasons);
				setRegistrationEnabled(activeRegitrationSeasons.length > 0);

				/*  const organizationPrograms = await organizationApi.getOrganizationProgramsByType(
          programData.organizationId,
          programData.type
        );
         setOtherPrograms(
          organizationPrograms.filter(
            program =>
              program.id !== Number(programId) && program.mainMedia !== null
          )
        );*/

				if (organization) {
					setContactData({ ...organization });
				}
				setLoading(false);
			} else {
				setLoading(false);
			}
		}

		fetchProgramData();
	}, [programId]); // eslint-disable-line react-hooks/exhaustive-deps

	const onSeasonSelected = (season: Season) => {
		const resourceSettings = season.resourceSettings ?? [];
		const hideTimes =
			getResourceSettingValueByKey(resourceSettings, ESessionResourceSettingKey.HIDE_EVENT_TIMES) === 'true';
		setIsHideEventTimes(hideTimes);
		seasonApi
			.getSubSeasons(Number(season.id))
			.then(subSeasonsData => setRegistrationEnabled(isRegistrationWindowOpenToAny(subSeasonsData)));
	};

	if (router.isFallback || isLoading) {
		return <div>{i18n.loading}</div>;
	}

	if (!programSport) {
		return <div>{i18n.loading}</div>;
	}
	const sportsArr = programSport ? [programSport] : [];
	const sportsIconsNames = getSportsIconsNames(sportsArr);

	return (
		<Page showHeroBg heroBgImg={programMainMedia} css={heroIconCss} title={heroTitle}>
			<Hero>
				<Hero.Summary title={heroTitle} description={heroDescription} sports={sportsIconsNames} tags={tags} />
				<Hero.Action>
					<SeasonPicker
						isRegistrationEnabled={isRegistrationEnabled || isLoading}
						program={heroTitle}
						seasons={activeRegitrationSeasons}
						sport={programSport}
						onSeasonSelected={onSeasonSelected}
						isHideEventTimes={isHideEventTimes}
					/>
				</Hero.Action>
			</Hero>

			<TwoColumnLayout>
				<TwoColumnLayout.Main>
					<TwoColumnLayout.Content>
						<LongDescription title={heroTitle} description={programLongDescription} />
						<ProgramSeasons programSeasons={futureSeasons} type={i18n.programs.comingUpTitle} />
						<ProgramSeasons programSeasons={activeSeasons} type={i18n.programs.activeSeasons} />
						{/*  <Conditional show={otherPrograms.length !== 0}>
              <div css={sectionCss}>
                <Catalog
                  title={`${i18n.programs.other} ${programType}`}
                  medias={otherPrograms}
                  showAccessories={false}
                  catalogType={rcEnums.catalogTypeEnum.PROGRAM}
                  prefixUrl={'../../'}
                />
              </div>
            </Conditional>*/}
						<ProgramSeasons programSeasons={pastSeasons} type={i18n.programs.pastSeasons} />
					</TwoColumnLayout.Content>
				</TwoColumnLayout.Main>
				<TwoColumnLayout.Sidebar>
					<ContactDetails contactData={contactData} />
				</TwoColumnLayout.Sidebar>
			</TwoColumnLayout>
		</Page>
	);
}

export function getStaticPaths(): GetStaticPathsResult {
	return {
		// See `docs/project-structure.md` for URL structure details.
		paths: [
			// {
			//   params: {
			//     programType: 'league',
			//     programSeoName: '[gender]-[youth/adult]-[activity]-[city]',
			//     programId: '1',
			//   },
			// },
		],
		fallback: true,
	};
}

export const getStaticProps: GetStaticProps = withStaticProps(() => {
	return {
		props: {
			activity: 'BasketBall',
		},
	};
});

export default Programs;
